import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useLiveData } from "@/context/LiveDataContext";
import { Play, Loader2, CheckCircle2, XCircle } from "lucide-react";

const TESTS = [
  { id: "health", label: "API Health", desc: "GET /health" },
  { id: "drones", label: "Liste drones", desc: "GET /drones" },
  { id: "status", label: "Statut DRONE-V1", desc: "GET /drones/DRONE-V1/status" },
  { id: "rtl", label: "RTL", desc: "Commande physique — via CommandCenter uniquement (pipeline LOT1)", disabled: true },
  { id: "arm", label: "ARM", desc: "Commande physique — via CommandCenter uniquement (pipeline LOT1)", disabled: true },
  { id: "takeoff", label: "TAKEOFF", desc: "Commande physique — via CommandCenter uniquement (bornes 1–3 m)", disabled: true },
  { id: "land", label: "LAND", desc: "Commande physique — via CommandCenter uniquement (pipeline LOT1)", disabled: true },
  { id: "goto", label: "GOTO", desc: "Non supporté par DRONE-V1 (legacy / missions autonomes futures)", disabled: true },
  { id: "simulate-rtl", label: "Simuler RTL vers base", desc: "GOTO non supporté par DRONE-V1 (legacy)", disabled: true },
];

export default function AdminTests() {
  const { drones } = useLiveData();
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(null);
  const [droneId, setDroneId] = useState("DRONE-V1");

  const run = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/test/${id}`, null, { params: { drone_id: droneId } });
      setResults((r) => ({ ...r, [id]: data }));
    } catch (e) {
      setResults((r) => ({ ...r, [id]: { ok: false, error: apiErr(e) } }));
    } finally { setBusy(null); }
  };

  const runAll = async () => { for (const t of TESTS) if (!t.disabled) await run(t.id); };

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-chivo text-3xl font-black tracking-tighter uppercase">Tests techniques</h1>
          <p className="font-mono text-xs text-[#A3A3A3] mt-1">Vérification de la passerelle drone (admin uniquement)</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A3A3A3] block mb-1">Drone cible</label>
            <select data-testid="test-drone-select" value={droneId} onChange={(e) => setDroneId(e.target.value)}
              className="bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-3 py-2.5 font-mono text-xs">
              {(drones.length ? drones.map((d) => d.drone_id) : ["DRONE-V1"]).map((id) => (
                <option key={id} value={id} className="bg-[#141414]">{id}</option>
              ))}
            </select>
          </div>
          <button data-testid="run-all-tests" onClick={runAll}
            className="flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-mono text-xs uppercase tracking-wider rounded-sm px-4 py-2.5">
            <Play size={14} /> Tout exécuter
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {TESTS.map((t) => {
          const res = results[t.id];
          return (
            <div key={t.id} data-testid={`test-${t.id}`} className="bg-[#141414] border border-white/10 rounded-sm p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {res ? (res.ok ? <CheckCircle2 size={16} className="text-[#22C55E]" /> : <XCircle size={16} className="text-[#EF4444]" />) : <span className="w-4 h-4 rounded-full border border-white/20" />}
                  <div>
                    <div className="font-mono text-sm">{t.label}</div>
                    <div className="font-mono text-[10px] text-[#525252]">{t.desc}</div>
                  </div>
                </div>
                <button data-testid={`run-test-${t.id}`} onClick={() => run(t.id)} disabled={busy === t.id || t.disabled}
                  title={t.disabled ? "Désactivé — commande physique via CommandCenter / GOTO non supporté (legacy)" : ""}
                  className="flex items-center gap-1.5 border border-white/15 hover:bg-white/10 rounded-sm font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 disabled:opacity-30 disabled:cursor-not-allowed">
                  {t.disabled ? "Désactivé" : busy === t.id ? <Loader2 size={12} className="animate-spin" /> : <><Play size={12} /> Run</>}
                </button>
              </div>
              {res && (
                <pre className="mt-2 bg-[#0A0A0A] border border-white/10 rounded-sm p-2 font-mono text-[10px] text-[#A3A3A3] overflow-x-auto max-h-40">
                  {JSON.stringify(res, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
